import { FC, useState, useEffect } from 'react';
import { Form, Button, Input } from 'antd';
import { IntlShape, useIntl } from 'react-intl';

import LazyImage from '../../../commons/components/LazyImage';
import { REGEX_EMAIL, REGEX_ONLY_NUMBER } from '../../../commons/constants';
import Loading from '../../../commons/components/Loading';
import { ContactInfoResponseType } from '../../../types';
import { getUserContactInfo, postUserContact } from '../../../services/saleContact.service';

import './styles.scss';

type RequestFormFieldsTypes = {
  name: string;
  fromUrl: string;
  phoneNumber: string;
  email: string;
  description: string;
};

type FormFieldsTypes = {
  name?: string;
  phoneNumber?: string;
  email?: string;
  description?: string;
};

type RequestFormPropsType = {
  token?: string;
  onFinishSubmit?: () => void;
};

const initialFields: RequestFormFieldsTypes = {
  name: '',
  fromUrl: '',
  phoneNumber: '',
  email: '',
  description: '',
};

const RequestForm: FC<RequestFormPropsType> = (props: RequestFormPropsType) => {
  const { onFinishSubmit, token } = props;
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [contactInfo, setContactInfo] = useState<ContactInfoResponseType>();

  const [form] = Form.useForm<RequestFormFieldsTypes>();
  // const { locale, setLocale } = useContext(IntlContext);
  const intl: IntlShape = useIntl();

  // TODO: remove when add language
  // const onChange = (value: string) => {
  //   if (setLocale) setLocale(value);
  // };

  const getInfoContact = async () => {
    setLoading(true);
    try {
      const { data } = await getUserContactInfo(token);
      setContactInfo(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const checkValidationForm = (values: FormFieldsTypes) => {
    const { description, email, name, phoneNumber } = values;
    let validation = true;
    const messageError = intl.formatMessage({ id: `contact.form.validation.required` });

    if (!name) {
      validation = false;
      form.setFields([
        {
          name: 'name',
          errors: [messageError],
          validating: validation,
        },
      ]);
    }

    if (!email) {
      validation = false;
      form.setFields([
        {
          name: 'email',
          errors: [messageError],
          validating: validation,
        },
      ]);
    }
    if (!description) {
      validation = false;
      form.setFields([
        {
          name: 'description',
          errors: [messageError],
          validating: validation,
        },
      ]);
    }
    if (!phoneNumber) {
      validation = false;
      form.setFields([
        {
          name: 'phoneNumber',
          errors: [messageError],
          validating: validation,
        },
      ]);
    }
    return validation;
  };

  const handleFormChange = (changedFields) => {
    if (!changedFields[0]) return;

    const fieldNotReset = (changedFields[0].name || []).find(
      (item) => item === 'email' || item === 'phoneNumber'
    );

    if (fieldNotReset) return;
    form.setFields([{ name: changedFields[0].name, validating: true, errors: [] }]);
  };

  const onFinish = async (values) => {
    if (checkValidationForm(values)) {
      try {
        setIsSubmitting(true);
        values.fromUrl = window.location.href;
        await postUserContact(values, token);
        form.resetFields();
        onFinishSubmit();
      } catch (error) {
        console.error(error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  useEffect(() => {
    if (token) getInfoContact();
  }, [token]);

  if (!contactInfo || !contactInfo.userContact) return null;

  const { userContact } = contactInfo;

  return (
    <div className="clf-request-form__content">
      <Loading loading={loading} />
      <div className="content__language">
        {/* TODO: remove when add language */}
        {/* <Select value={locale} onChange={onChange} className="language__selected">
            {Object.values(LANGUAGES).map((language) => (
              <Option key={language.key} value={language.key} className="selected__item">
                {language.icon}
                {language.label}
              </Option>
            ))}
          </Select> */}
      </div>
      <div className="content__contact-info">
        {userContact.avatar && (
          <div className="contact-info__avatar">
            <LazyImage image={userContact.avatar}></LazyImage>
          </div>
        )}
        {userContact.name && (
          <div className="contact-info__name title-18-bold">{userContact.name}</div>
        )}
        {userContact.jobPosition && (
          <div className="contact-info__job-title">{userContact.jobPosition}</div>
        )}
        {userContact.phoneNumber && (
          <Button href={`tel:${userContact.phoneNumber}`} className="contact-info__phone-number">
            <i className="clf-icon-phone_24x24"></i>
            {userContact.phoneNumber}
          </Button>
        )}
      </div>
      <div className="content__title-info title-18-bold">
        {intl.formatMessage({ id: `contact.form.request-info` })}
      </div>
      <Form
        form={form}
        initialValues={initialFields}
        className="content__form"
        onFieldsChange={handleFormChange}
        onFinish={onFinish}
      >
        <Form.Item name="name">
          <Input
            placeholder={intl.formatMessage({ id: `contact.form.name` })}
            value={initialFields.name}
          />
        </Form.Item>
        <Form.Item
          name="email"
          rules={[
            {
              required: true,
              validator: async (_, value) => {
                if (value && !new RegExp(REGEX_EMAIL).test(value)) {
                  throw new Error(intl.formatMessage({ id: `contact.form.validation.email` }));
                }
              },
            },
          ]}
        >
          <Input
            placeholder={intl.formatMessage({ id: `contact.form.email` })}
            value={initialFields.email}
          />
        </Form.Item>
        <Form.Item
          name="phoneNumber"
          rules={[
            {
              required: true,
              validator: async (_, value) => {
                if (value && !new RegExp(REGEX_ONLY_NUMBER).test(value)) {
                  throw new Error(intl.formatMessage({ id: `contact.form.validation.phone` }));
                }
              },
            },
          ]}
        >
          <Input
            placeholder={intl.formatMessage({ id: `contact.form.phone` })}
            value={initialFields.phoneNumber}
          />
        </Form.Item>
        <Form.Item name="description">
          <Input.TextArea
            className="form__introduction"
            rows={3}
            placeholder={userContact.shortIntro}
            value={initialFields.description}
          />
        </Form.Item>
        <div className="form__footer">
          <Button type="primary" htmlType="submit" className="footer__btn" loading={isSubmitting}>
            {intl.formatMessage({ id: `contact.form.submit` })}
          </Button>
          <div className="footer__privacy-policy caption-12">
            {intl.formatMessage({ id: `contact.form.privacy-policy` })}
          </div>
        </div>
      </Form>
    </div>
  );
};

export default RequestForm;
